"use client";

import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import TravelerPicker, {
  type TravelerCounts,
} from "@/components/search-form/fields/TravelerPicker";
import { Typography } from "@/components/Typography";

export interface FormTravelerFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  placeholder?: string;
  leadingIcon?: React.ReactNode;
  disabled?: boolean;
  rounded?: "normal" | "full";
}

const parseTravelerCounts = (value: unknown): TravelerCounts => {
  if (typeof value === "object" && value !== null && "adults" in value) {
    const v = value as Partial<TravelerCounts>;
    return {
      adults: typeof v.adults === "number" ? v.adults : 2,
      children: typeof v.children === "number" ? v.children : 0,
      rooms: typeof v.rooms === "number" ? v.rooms : 1,
    };
  }

  if (typeof value === "string") {
    const adultsMatch = value.match(/(\d+)\s*adult/i);
    const childrenMatch = value.match(/(\d+)\s*(?:child|children)/i);
    const roomsMatch = value.match(/(\d+)\s*room/i);

    return {
      adults: adultsMatch ? parseInt(adultsMatch[1], 10) : 2,
      children: childrenMatch ? parseInt(childrenMatch[1], 10) : 0,
      rooms: roomsMatch ? parseInt(roomsMatch[1], 10) : 1,
    };
  }

  return { adults: 2, children: 0, rooms: 1 };
};

const formatTravelerCounts = (counts: TravelerCounts): string => {
  const parts: string[] = [];
  parts.push(`${counts.adults} ${counts.adults === 1 ? "adult" : "adults"}`);
  if (counts.children > 0) {
    parts.push(
      `${counts.children} ${counts.children === 1 ? "child" : "children"}`,
    );
  }
  parts.push(`${counts.rooms} ${counts.rooms === 1 ? "room" : "rooms"}`);
  return parts.join(" · ");
};

const FormTravelerField = <TFieldValues extends FieldValues>({
  name,
  control,
  label = "Guests",
  leadingIcon,
  rounded = "normal",
}: FormTravelerFieldProps<TFieldValues>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => {
      const currentCounts = parseTravelerCounts(field.value);

      const handleChange = (counts: TravelerCounts) => {
        field.onChange(formatTravelerCounts(counts));
      };

      return (
        <div className="flex flex-col gap-1.5">
          {label && <Typography variant="button-sm">{label}</Typography>}
          <TravelerPicker
            value={currentCounts}
            onChange={handleChange}
            rounded={rounded}
            leadingIcon={leadingIcon}
            error={!!fieldState.error}
          />
          {fieldState.error && (
            <Typography variant="body2" className="text-error-foreground">
              {fieldState.error.message}
            </Typography>
          )}
        </div>
      );
    }}
  />
);

export default FormTravelerField;
